import React, { createContext, useState, useContext, useEffect } from 'react';
import { api } from '../config/api';
import { setAnalyticsUserId, clearAnalyticsUserId, trackEvent } from '../utils/analytics';

export interface InstitutionMembership {
  institution_id: string;
  institution_name: string;
  institution_slug: string;
  role: 'institution_admin' | 'institution_sub_admin' | 'teacher' | 'student';
  group_id: string | null;
  group_name: string | null;
}

export type SupportedLanguage = 'english' | 'hindi' | 'gujarati' | 'marathi';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  name?: string;  // Display name derived from full_name
  avatar_url?: string;  // User avatar URL
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  google_id?: string | null;
  language: SupportedLanguage;
  institution_memberships?: InstitutionMembership[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, full_name?: string, referral_code?: string) => Promise<void>;
  googleLogin: (credential: string, referral_code?: string) => Promise<void>;
  logout: () => void;
  updateLanguage: (language: SupportedLanguage) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await api.get('/api/auth/me');
        // Map full_name to name for easier access
        const userData = {
          ...response.data,
          name: response.data.full_name || response.data.email.split('@')[0]
        };
        setUser(userData);
        setAnalyticsUserId(userData.id);
      } catch (error) {
        localStorage.removeItem('access_token');
      }
    }
    setLoading(false);
  };

  const login = async (email: string, password: string) => {
    const response = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('access_token', response.data.access_token);
    await checkAuth();
    trackEvent('login', { method: 'email' });
  };

  const signup = async (email: string, password: string, full_name?: string, referral_code?: string) => {
    await api.post('/api/auth/signup', { email, password, full_name, referral_code: referral_code || undefined });
    // After signup, log the user in
    await login(email, password);
    trackEvent('sign_up', { method: 'email', referral_code: referral_code || undefined });
  };

  const googleLogin = async (credential: string, referral_code?: string) => {
    const payload = { credential, referral_code: referral_code || undefined };
    // Safari sometimes has timing issues with Google tokens
    // Retry once after a short delay if it fails
    try {
      const response = await api.post('/api/auth/google', payload);
      localStorage.setItem('access_token', response.data.access_token);
      await checkAuth();
      trackEvent('login', { method: 'google', referral_code: referral_code || undefined });
    } catch (error: any) {
      // If Safari timing issue, wait and retry once
      if (error.response?.status === 401 && /safari/i.test(navigator.userAgent)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const response = await api.post('/api/auth/google', payload);
        localStorage.setItem('access_token', response.data.access_token);
        await checkAuth();
        trackEvent('login', { method: 'google', referral_code: referral_code || undefined });
      } else {
        throw error;
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    clearAnalyticsUserId();
  };

  const updateLanguage = async (language: SupportedLanguage) => {
    const response = await api.patch('/api/auth/me/preferences', { language });
    setUser((prev) => (prev ? { ...prev, language: response.data.language } : prev));
    trackEvent('language_changed', { language });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, googleLogin, logout, updateLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}; 